<template>
	<ShopView>
		<!-- TODO: Remove after cleanup -->
		<AsiCard v-if="topCategoriesCarousel.length === 0" no-bottom-margin flat :class="['asi-card', {'mobile': !sDesktop}]">
			<v-card-text :class="['shop-header', 'd-flex', {'flex-column': !sDesktop}, {'align-center': sDesktop}]">
				<div :class="{'mobile': !sDesktop}">
					<h1 class="shop-title font-weight-bold ma-0">
						{{ $t('homepage.welcomeIn') }} <br>
						{{ $isMkShop ?  $t('homepage.headingMK') : $t('homepage.headingHerzog')}}
					</h1>
					<p :class="['headline', 'mt-9', {'pr-12': sDesktop}, {'mr-9': sDesktop}]">{{ headline }}</p>
				</div>
				<div :class="{'mobile': !sDesktop}">
					<div v-if="$isHerzogShop" :class="['video-wrapper', {'mobile': sMobile}]">
						<VideoCard class="video" :source="videoSource" :thumbnail-src="herzogVideoThumbnail"/>
						<v-img v-if="sDesktop" class="silvio" :src="silvioPic" max-width="7vw" contain></v-img>
					</div>
					<AsiCard v-else class="header-img" unwrapped>
						<v-img :src="headerImageMk" width="100%" min-height="12rem"></v-img>
					</AsiCard>
				</div>
			</v-card-text>
		</AsiCard>

		<template v-if="topCategories.length > 0">
			<div class="shop-header-wrapper">
				<h1 class="shop-title font-weight-bold ma-0">
					{{ $t('homepage.welcomeIn') }} <br>
					{{ $isMkShop ?  $t('homepage.headingMK') : $t('homepage.headingHerzog')}}
				</h1>
			</div>

			<v-container v-if="topCategories.length > 0" class="d-flex flex-grow-1 flex-row justify-center">
				<v-carousel class="carousel-wrapper"
				            :class="{ mobile: sMobile }"
				            :show-arrows-on-hover="!sMobile"
				            :show-arrows="!sMobile"
				            cycle
				            height="100%"
				            interval="8000"
				            hide-delimiter-background
				>
					<template v-slot:prev="{ on, attrs }">
						<v-btn
							fab
							small
							color="white"
							class="carousel-arrow"
							v-bind="attrs"
							v-on="on"
						>
							<v-icon color="#222">{{ icons.prev }}</v-icon>
						</v-btn>
					</template>

					<template v-slot:next="{ on, attrs }">
						<v-btn
							fab
							small
							color="white"
							class="carousel-arrow"
							v-bind="attrs"
							v-on="on"
						>
							<v-icon color="#222">{{ icons.next }}</v-icon>
						</v-btn>
					</template>
					<v-carousel-item v-for="category in topCategoriesCarousel"
					                 :key="category.id">
						<router-link :to="detailRoute(category)" class="text-decoration-none" style="color: inherit">
							<div v-if="carouselAttachmentOrFallback(category)" class="carousel-item">
								<AsiCard unwrapped no-bottom-margin class="d-flex flex-column overflow-hidden">
									<v-progress-linear :value="100" :indeterminate="loadingImage" class="rounded-0"/>
									<v-img :src="carouselImageSrcLarge(category)"
									       :lazy-src="carouselImageSrcSmall(category)"
									       class="carousel-item-image"
									       :height="sMobile ? '300px' : '400px'"
									       :width="sMobile ? '100%' : '400px'"
									       :alt="category.name[lang]"
									       eager
									       @loadstart="loadingImage = true"
									       @load="loadingImage = false"
									       @error="loadingImage = false"
									>
									</v-img>
								</AsiCard>
								<div class="carousel-item-text">
									<span class="overline black--text my-0 line-height-1 font-weight-regular">
										{{ $t('category.isTopCategory') }}
									</span>
									<h3 class="text-left shop-subtitle">{{ category.name[lang] }}</h3>
									<AsiMarkDownViewer class="mt-2 clamp-text max-w-full"
									                   v-if="category.carouselDescription && category.carouselDescription[lang]"
									                   :value="category.carouselDescription[lang]"/>
									<router-link :to="detailRoute(category)" class="mt-2 text-decoration-none primary--text"
									             @click.native.stop>
										{{ $t('category.terms.goToCategory') }}
									</router-link>
								</div>
							</div>
						</router-link>
					</v-carousel-item>
				</v-carousel>
			</v-container>
		</template>

		<CategoryNavigation :class="{'category-navigation': sDesktop}"/>

		<VideoCardDialog v-if="$isHerzogShop" :source="videoSource" :open="showVideoDialog" @cancel="videoDismissed"/>
	</ShopView>
</template>

<script lang="ts">
	import {Component} from "vue-property-decorator";
	import AsiBtn from "@/components/common/AsiBtn.vue";
	import CategoryNavigation from "@/components/category/shop/CategoryNavigation.vue";
	import ShopView from "@/components/layout/ShopView.vue";
	import {mixins} from "vue-class-component";
	import RightChecks from "@/mixins/RightChecks.vue";
	import AsiCard from "@/components/common/AsiCard.vue";
	import LocaleHelper from "@/helpers/LocaleHelper";
	import ResponsiveChecks from "@/mixins/ResponsiveChecks.vue";
	import Icon from "@/plugins/icons";
	import VideoCard from "@/components/common/VideoCard.vue";
	import VideoCardDialog from "@/components/common/VideoCardDialog.vue";
	import {ICategoryShopListEntry} from "@/models/category/CategoryShopModels";
	import AttachmentHelper from "@/models/attachment/AttachmentHelper";
	import {AttachmentMediaSize} from "@/helpers/constants";
	import AsiMarkDownViewer from "@/components/common/AsiMarkDownViewer.vue";
	import CategoryHelper from "@/models/category/CategoryHelper";
	import {RawLocation} from "vue-router";
	import {IAttachment} from "@/models/attachment/AttachmentModels";

	@Component({
		components: {AsiMarkDownViewer, VideoCardDialog, VideoCard, AsiCard, ShopView, CategoryNavigation, AsiBtn}
	})
	export default class Home extends mixins(RightChecks, ResponsiveChecks) {

		private data: Map<string, string> = new Map<string, string>();

		private carouselCategories: Array<ICategoryShopListEntry> = [];

		private icons = Icon;
		private locale: string = this.$i18n.locale;

		private loadingImage = false;
		private showVideoDialog = false;
		private herzogVideoThumbnail = 'https://prodchnorth02908337ae324.blob.core.windows.net/herzog-dichtungen-poesia-webshop-assets/herzog-video/thumbnail.png';
		private silvioPic = 'https://prodchnorth02908337ae324.blob.core.windows.net/herzog-dichtungen-poesia-webshop-assets/herzog-video/silvio.png';
		private headerImageMk = 'https://prodchnorth02fdec83b4882.blob.core.windows.net/mk-dichtungen-poesia-webshop-assets/header-image.jpg';

		private get lang(): string {
			return this.$i18n.locale === null ? 'de' : LocaleHelper.extractLanguage(this.$i18n.locale);
		}

		private get headline(): string | null {
			return this.data.get(this.lang) ?? null;
		}

		private get videoSource(): string {
			switch (this.lang) {
				case 'de':
					return 'https://prodchnorth02908337ae324.blob.core.windows.net/herzog-dichtungen-poesia-webshop-assets/herzog-video/webshop-herzog-dichtungen-de.mp4';
				case 'en':
					return 'https://prodchnorth02908337ae324.blob.core.windows.net/herzog-dichtungen-poesia-webshop-assets/herzog-video/webshop-herzog-dichtungen-en.mp4';
				case 'fr':
					return 'https://prodchnorth02908337ae324.blob.core.windows.net/herzog-dichtungen-poesia-webshop-assets/herzog-video/webshop-herzog-dichtungen-fr.mp4';
				default:
					return 'https://prodchnorth02908337ae324.blob.core.windows.net/herzog-dichtungen-poesia-webshop-assets/herzog-video/webshop-herzog-dichtungen-de.mp4';
			}
		}

		private get videoWatched(): boolean {
			return this.$store.state.ui.videoWatched;
		}

		public created(): void {
			this.init();
		}

		private avatarAttachment(category: ICategoryShopListEntry): IAttachment | null {
			return category.galleryAttachments.find(i => i.id === category.avatarAttachmentId) ?? null;
		}

		private carouselAttachment(category: ICategoryShopListEntry): IAttachment | null {
			return category.galleryAttachments.find(i => i.id === category.carouselAttachmentId) ?? null;
		}

		private carouselAttachmentOrFallback(category: ICategoryShopListEntry): IAttachment | null {
			return this.carouselAttachment(category) ?? this.avatarAttachment(category);
		}

		private carouselImageSrcSmall(category: ICategoryShopListEntry): string | null {
			const attachment = this.carouselAttachmentOrFallback(category);

			return attachment ? AttachmentHelper.getImageUrl(attachment, AttachmentMediaSize.s)	: null;
		}

		private carouselImageSrcLarge(category: ICategoryShopListEntry): string | null {
			const attachment = this.carouselAttachmentOrFallback(category);

			return attachment ? AttachmentHelper.getImageUrl(attachment, AttachmentMediaSize.l)	: null;
		}

		private init(): void {
			if (this.$isHerzogShop) {
				this.showVideoDialog = !this.videoWatched;
			}

			if (this.$isHerzogShop) {
				this.data.set('en',
					'In our new webshop you can order seals quickly and easily at any time. Find O-rings, shaft seals, grooved rings,\n' +
					'flat seals, hose seals and much more for a wide range of applications in the industrial sector - always in proven\n' +
					'top quality.');

				this.data.set('fr',
					'Dans notre nouvelle boutique en ligne, vous pouvez commander des joints à tout moment, rapidement et facilement. ' +
					'Vous trouverez des joints toriques, des joints d\'arbre, des bagues rainurées, des joints plats, des joints de\n' +
					'tuyau et bien d\'autres choses encore pour les applications les plus diverses dans le domaine de l\'industrie -\n' +
					'toujours dans une qualité de pointe éprouvée.');

				this.data.set('de',
					'In unserem neuen Webshop bestellen Sie Dichtungen jederzeit schnell und einfach. ' +
					'Finden Sie O-Ringe, Wellendichtungen, Nutringe, Flachdichtungen, Schlauchdichtungen uvm. für unterschiedlichste Anwendung im\n' +
					'Industrie-Bereich – stets in bewährter Top-Qualität.');

				this.data.set('it',
					'Ordina guarnizioni in modo semplice e veloce, in qualsiasi momento, nel nostro nuovo negozio online. ' +
					'Trova O-ring, guarnizioni per alberi, U-ring, guarnizioni piatte, guarnizioni per tubi flessibili e molto altro ancora per un\'ampia varietà di applicazioni nel settore industriale.\n' +
					'Sempre con la massima qualità garantita.');
			}
			if (this.$isMkShop) {
				this.data.set('en',
					'mk dichtungs ag supplies its customers with almost 5000 seal profiles for all conceivable applications in the ' +
					'fields of building, refrigeration/air conditioning technology, automotive engineering, power plants, rolling stock ' +
					'and transport. Many years of experience and know-how as well as a wide range of new, replacement and retrofit seals ' +
					'for metal frames, doors, windows, showers, fridges, refrigerated drawers and many other applications.');

				this.data.set('fr',
					'mk dichtungs ag propose à ses clients  près de 5000 profilés d’étanchéité pour toutes les applications possibles ' +
					'et imaginables dans les secteurs du bâtiment, de la réfrigération et de la climatisation, de l’industrie, de l’industrie ' +
					'automobile, des centrales électriques, des véhicules ferroviaires et des transports. Une longue expérience et un ' +
					'savoir faire, ainsi qu’un large assortiment de joints neufs, de remplacement et de rajout pour les huisseries métalliques, ' +
					'les portes, fenêtres, douches, réfrigérateurs, tiroirs de meubles réfrigérés et pour de nombreuses autres utilisations.');

				this.data.set('de',
					'Die mk dichtungs ag versorgt ihre Kunden mit fast 5000 Dichtungsprofilen für alle denkbaren Anwendungen in den ' +
					'Bereichen Bau, Kälte-/Klimatechnik, Industrie, Fahrzeugbau, Kraftwerk, Schienenfahrzeug und Transport. Langjährige ' +
					'Erfahrung und Know-how sowie grosses Sortiment an Neu-, Ersatz- und Nachrüstdichtungen für Metallzargen, Türen, Fenster,' +
					' Duschen, Kühlschränke, Kühlschubladen und für viele weitere Anwendungen.');

				this.data.set('it',
					'MK Dichtungs AG fornisce ai suoi clienti quasi 5000 profili di tenuta per tutte le applicazioni immaginabili nel settore ' +
					'Settori di competenza: edilizia, refrigerazione/condizionamento, industria, produzione di veicoli, centrali elettriche, ' +
					'veicoli ferroviari e trasporti. Vanta una pluriennale esperienza. Esperienza e competenza, oltre a una vasta gamma di ' +
					'guarnizioni nuove, di ricambio e di retrofit per telai metallici, porte, finestre, docce, frigoriferi, cassetti ' +
					'frigoriferi e per molte altre applicazioni.');
			}


		}

		private videoDismissed(): void {
			this.showVideoDialog = false;

			this.$store.commit('ui/setVideoWatched', true);
		}

		private get topCategories(): ICategoryShopListEntry[] {
			return this.$store.getters['category/topCategories'];
		}

		private get topCategoriesCarousel(): ICategoryShopListEntry[] {
			return this.topCategories.filter(i => i.carouselAttachmentId !== null || i.avatarAttachmentId !== null);
		}

		private detailRoute(category: ICategoryShopListEntry): RawLocation | null {
			return CategoryHelper.detailRoute(CategoryHelper.currentCanonical(category.canonical));
		}
	}
</script>

<style lang="scss" scoped>
	@import '../../../../node_modules/vuetify/src/styles/styles';

	/* TODO: Remove after cleanup */
	.asi-card {
		margin-top: $spacer * 20;
		background-color: transparent;

		&.mobile {
			margin-top: $spacer * 2;
		}

		.shop-header {
			height: 100%;

			.shop-title {
				font-size: 32pt;
				line-height: 33pt;
			}

			div {
				width: 50%;
				flex: 1;
				box-sizing: border-box;

				&.mobile {
					width: 100%;
				}

				.header-img {
					width: 100%;
					height: auto;
					overflow: hidden;
				}

				.video-wrapper {
					position: relative;
					width: 100%;
					margin-bottom: 5rem;
					margin-right: 5rem;

					&.mobile {
						max-width: 100%;
						margin-right: 0;
					}

					.video {
						width: auto;
						overflow: hidden;
					}

					.silvio {
						position: absolute;
						top: 40%;
						left: 83%;
						z-index: 2;
					}
				}
			}
		}
	}

	.category-navigation {
		margin-top: $spacer * 16;
	}

	.shop-title {
		font-size: 2.25rem;
		line-height: 2.25rem;
	}

	.shop-subtitle {
		font-size: 1.5rem;
		line-height: 1.5rem;
	}

	.shop-header-wrapper {
		display: flex;
		flex-direction: row;
		justify-content: start;
		justify-self: center;
		width: 100%;
		max-width: 1200px;
	}

	.carousel-wrapper {
		max-width: 1200px;
		width: 100%;
		height: 100%;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		align-items: stretch;
		justify-content: center;
		margin-top: $spacer * 6;;
		padding-bottom: 64px;

		.clamp-text {
			display: -webkit-box;
			-webkit-line-clamp: 7;
			-webkit-box-orient: vertical;
			overflow: hidden;
		}

		.carousel-item {
			display: grid;
			margin-inline: auto;
			width: 100%;
			grid-template-columns: minmax(0, 400px) minmax(0, 400px);
			justify-content: center;
			align-items: start;
			grid-gap: $spacer * 8;
		}

		.carousel-item-text {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			overflow: hidden;
			overflow-wrap: break-word;
		}
	}

	.carousel-wrapper.mobile {
		height: 100%;
		min-height: 550px;
		padding-bottom: 64px;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: start;

		.clamp-text {
			display: -webkit-box;
			-webkit-line-clamp: 5;
			-webkit-box-orient: vertical;
			overflow: hidden;
		}

		.carousel-item {
			height: 100%;
			display: grid;
			grid-gap: 2rem;
			grid-template-columns: 1fr;
			gap: 16px;
		}

		.carousel-item-text {
			max-width: 100%;
		}
	}

	.carousel-arrow:hover {
		transform: scale(1.1);
	}

	::v-deep .v-carousel__controls__item {
		opacity: 0.8;

		.v-icon {
			color: rgba(0, 0, 0, .35) !important;
			transition: color .2s ease, transform .2s ease, opacity .2s ease;
		}

		&.v-btn--active .v-icon {
			color: #222;
			transform: scale(1.25);
			opacity: 0.8;
		}
	}
</style>
